import React from 'react';
import { Checkbox, Input, Select as AntDSelect, SelectProps as AntDSelectProps } from 'antd';
import debounce from 'lodash/debounce';
import uniqBy from 'lodash/uniqBy';
import differenceBy from 'lodash/differenceBy';
import classNames from 'classnames';

import { DefaultContentType, RawValue, SelectedState } from '../types';
import { maybeCapitalizeFirstLetter } from 'components/AntD/Dropdown/utils';
import { getScrollParent } from '../utils';
import { SolidCaretDown } from 'Icons';
import slColors from 'sl-colors';
import './styles.less';

const { Option } = AntDSelect;
export interface SelectProps<VT = DefaultContentType>
  extends Partial<Omit<AntDSelectProps<Array<RawValue>>, 'onChange'>> {
  contents: Array<VT>;
  selectRef?(node: HTMLSpanElement): void;
  selected?: VT | Array<VT> | string | Array<string>;
  idKey?: keyof VT;
  labelKey?: keyof VT;
  optionRender?: (item: VT) => React.ReactNode;
  labelRender?: (item: VT) => React.ReactNode;
  onChange?(selected: Array<VT> | RawValue, item?: VT): void;
  label?: React.ReactNode;
  filterCallback?(keyword: string): unknown;
  disableSearch?: boolean;
  showCaret?: boolean;
  labelClassName?: string;
  dropdownClassName?: string;
  bordered?: boolean;
  disableCheckbox?: boolean;
  selectClassName?: string;
  dropdownMatchSelectWidth?: boolean;
}

export default function Select<I = DefaultContentType>({
  contents,
  id,
  mode,
  selected,
  label,
  onChange,
  selectRef,
  optionRender,
  labelRender,
  labelClassName = '',
  dropdownClassName = '',
  selectClassName = '',
  placeholder = label ? '' : 'Search',
  className = '',
  labelKey = 'label' as keyof I,
  idKey = 'id' as keyof I,
  disableSearch = false,
  disableCheckbox = false,
  showCaret = false,
  filterCallback = () => void 0,
  bordered = false,
  dropdownMatchSelectWidth = false,
  suffixIcon = <i className="fas fa-angle-down" />,
  ...props
}: SelectProps<I>): JSX.Element {
  const [searchTerm, setSearchTerm] = React.useState('');
  const [focused, setFocused] = React.useState(false);
  const [items, setItems] = React.useState(contents);
  const value = React.useMemo<RawValue[]>(() => {
    if (typeof selected === 'string' || typeof selected === 'number') {
      return [selected];
    }
    if (Array.isArray(selected)) {
      return selected.map(item => {
        if (typeof item === 'number' || typeof item === 'string') {
          return item;
        }
        return item[idKey] as unknown as RawValue;
      });
    }

    return selected ? [selected[idKey] as unknown as RawValue] : [];
  }, [idKey, selected]);

  React.useEffect(() => {
    setItems(contents);
  }, [contents]);

  const debouncedFilterCallback = React.useMemo(
    () =>
      debounce((input: string) => {
        setItems(
          contents.filter(c =>
            JSON.stringify(Object.values(c)).toLowerCase().includes(input?.toLowerCase())
          )
        );
      }, 250),
    [contents]
  );
  const allSelected = React.useMemo<SelectedState>(() => {
    let hasOne = false;
    const hasAll = contents.every(({ [idKey]: id }) => {
      const includes = value.includes(id as unknown as RawValue);
      hasOne = hasOne || includes;
      return includes;
    });

    return hasAll ? SelectedState.ALL : hasOne ? SelectedState.INDETERMINATE : SelectedState.NONE;
  }, [contents, idKey, value]);

  const onSelectChange = React.useCallback(
    (values, updated) => {
      if (!onChange) {
        return;
      }
      const options = updated as Array<{ item: I }>;
      if (mode === 'tags') {
        onChange(
          values.map(
            (str: string) =>
              items.find(({ [idKey]: id }) => (id as unknown as string) === str) || {
                [idKey]: str,
              }
          )
        );
      } else if (mode === 'multiple') {
        const selectedValues = selected as Array<I>;
        if (values.includes(SelectedState.ALL)) {
          const keys = values.filter((id: string) => id !== SelectedState.ALL) as Array<RawValue>;
          if (allSelected === SelectedState.ALL) {
            const selectedKeys = keys.filter(
              value => !items.find(({ [idKey]: id }) => (id as unknown as RawValue) === value)
            );
            onChange(
              selectedValues.filter(({ [idKey]: id }) =>
                selectedKeys.includes(id as unknown as RawValue)
              )
            );
          } else {
            onChange(uniqBy(selectedValues.concat(items), idKey));
          }
        } else {
          onChange(
            options.map(({ item }) => item),
            differenceBy(
              options.length > selectedValues.length
                ? options.map(({ item }) => item)
                : selectedValues,
              options.length > selectedValues.length
                ? selectedValues
                : options.map(({ item }) => item),
              idKey
            )[0]
          );
        }
      } else {
        onChange(values as RawValue, updated?.item);
      }
    },
    [onChange, mode, allSelected, selected, items, idKey]
  );

  return (
    <span
      role="presentation"
      id={`${id}-wrapper`}
      ref={selectRef}
      className={classNames(
        'sl-select-wrapper tw-cursor-pointer',
        {
          'tw-relative': !!label,
        },
        className
      )}
      onClick={e => {
        e.stopPropagation();
      }}
    >
      {label && focused && !disableSearch && (
        <label
          className="tw-absolute tw-text-xs tw-mb-0 tw-mt-0 tw-text-gray-700 tw-font-medium"
          htmlFor={id}
        >
          {maybeCapitalizeFirstLetter(label)}
        </label>
      )}
      <AntDSelect
        className={classNames('sl-select tw-w-full', selectClassName)}
        id={id}
        mode={mode}
        placeholder={placeholder}
        dropdownClassName={classNames(
          'sl-select-dropdown tw-z-1250',
          {
            [mode as string]: mode,
          },
          dropdownClassName
        )}
        fieldNames={{
          value: idKey as string,
        }}
        {...(label && {
          getRawInputElement: () =>
            !disableSearch && focused ? (
              <span>
                <Input
                  id={`${id}-input`}
                  value={searchTerm}
                  onChange={({ target: { value: inputValue } }) => {
                    setSearchTerm(inputValue);
                    const hasNetworkFilter = filterCallback(inputValue);
                    if (!hasNetworkFilter) {
                      debouncedFilterCallback(inputValue as string);
                    }
                  }}
                  bordered={false}
                  // eslint-disable-next-line jsx-a11y/no-autofocus
                  autoFocus
                />
              </span>
            ) : (
              <label
                className={classNames(
                  'tw-mb-0 tw-cursor-pointer tw-flex tw-flex-row tw-items-center tw-gap-1 tw-text-sm tw-font-medium tw-text-gray-700',
                  labelClassName
                )}
                htmlFor={id}
              >
                {maybeCapitalizeFirstLetter(label)}
                {showCaret && <SolidCaretDown width={12} height={12} color={slColors.gray[300]} />}
              </label>
            ),
        })}
        getPopupContainer={triggerNode => getScrollParent(triggerNode)}
        suffixIcon={suffixIcon}
        clearIcon={<i className="fas fa-times" />}
        onChange={onSelectChange}
        onDropdownVisibleChange={setFocused}
        dropdownMatchSelectWidth={dropdownMatchSelectWidth}
        value={value}
        filterOption={false}
        bordered={bordered}
        {...(mode === 'multiple' &&
          !disableCheckbox && {
            menuItemSelectedIcon: ({ isSelected }: { isSelected: boolean }) => (
              <Checkbox checked={isSelected} />
            ),
          })}
        showArrow={showCaret}
        {...props}
      >
        {mode === 'multiple' && searchTerm && items.length > 0 ? (
          <>
            <Option
              className="select-all-option"
              key={SelectedState.ALL}
              value={SelectedState.ALL}
              {...{
                [idKey]: SelectedState.ALL,
              }}
            >
              <Checkbox
                checked={allSelected === SelectedState.ALL}
                indeterminate={allSelected === SelectedState.INDETERMINATE}
              />
              Select All
            </Option>
          </>
        ) : (
          <></>
        )}
        {items.map(option => {
          const label = maybeCapitalizeFirstLetter(
            labelRender ? labelRender(option) : option[labelKey]
          );
          const content = optionRender ? optionRender(option) : label;

          return (
            <Option
              className="tw-mx-1 hover:tw-bg-gray-100 tw-rounded-sm"
              value={option[idKey] as unknown as RawValue}
              key={option[idKey] as unknown as RawValue}
              label={label}
              item={option}
              disabled={!!(option as unknown as DefaultContentType).disabled}
              {...{
                [idKey]: option[idKey] as unknown as RawValue,
              }}
            >
              {content}
            </Option>
          );
        })}
      </AntDSelect>
    </span>
  );
}
